/** @format */

import {
	Box,
	Card,
	Chip,
	Divider,
	Grid,
	InputAdornment,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
	IconButton,
	Tooltip,
	CircularProgress,
	Tab,
	Tabs,
	Alert,
	Avatar,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Modal,
	Fade,
	Backdrop,
	Popover,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import OutlinedInput from "@mui/material/OutlinedInput";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/ssr/MagnifyingGlass";
import { ArrowsClockwise } from "@phosphor-icons/react/dist/ssr/ArrowsClockwise";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ReplayIcon from "@mui/icons-material/Replay";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import TodayIcon from "@mui/icons-material/Today";
import GroupIcon from "@mui/icons-material/Group";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonIcon from "@mui/icons-material/Person";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React, { useEffect, useState, useCallback } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store/store";
import {
	getPayoutOverviewAsAdmin,
	getRetryablePayoutsAsAdmin,
	retryPayoutsForDate,
	retrySpecificPayout,
	retryAllPayouts,
	getCirclePayoutDetailsAsAdmin,
	PayoutRecord,
	RetryablePayoutRecord,
	CirclePayoutSummary,
	CirclePayoutDetailsResponse,
	CirclePayoutMonthGroup,
} from "../../../redux/actions/adminPayOutAction";
import {
	selectPayoutOverview,
	selectRetryablePayouts,
	selectAdminPayoutLoading,
	selectAdminPayoutError,
	selectRetryLoading,
	selectLastRetryResult,
	selectBulkRetryResult,
	selectCirclePayoutDetailsLoading,
} from "../../../redux/selectors/payoutSelector";
import { LoadingSpinner } from "../../allComponents/AllComponents";
import UserProfileHeader from "../../userprofile-header/UserProfileHeader";
import DrawerNavbar from "../../drawerNavBar/DrawerNavbar";
import Main from "../../../utility/common/Main";
import { formatCurrency } from "../../../utility/currencyUtils";

// Tab types
type TabValue =
	| "overview"
	| "completed"
	| "missed"
	| "failed"
	| "today"
	| "upcoming"
	| "retryable";

const PayOutReports = () => {
	const dispatch = useDispatch<AppDispatch>();

	// Selectors
	const payoutOverview = useSelector(selectPayoutOverview);
	const retryablePayouts = useSelector(selectRetryablePayouts);
	const isLoading = useSelector(selectAdminPayoutLoading);
	const error = useSelector(selectAdminPayoutError);
	const isRetrying = useSelector(selectRetryLoading);
	const lastRetryResult = useSelector(selectLastRetryResult);
	const bulkRetryResult = useSelector(selectBulkRetryResult);
	const isLoadingCircleDetails = useSelector(selectCirclePayoutDetailsLoading);

	// Also get from RootState for loading states
	const isLoadingOverview = useSelector(
		(state: RootState) => state.adminPayout?.isLoadingOverview ?? false,
	);

	// UI State
	const [openNav, setOpenNav] = useState(false);
	const [activeTab, setActiveTab] = useState<TabValue>("overview");
	const [searchQuery, setSearchQuery] = useState("");
	const [dismissedError, setDismissedError] = useState(false);

	// Circle Payout Modal State
	const [circleModalOpen, setCircleModalOpen] = useState(false);
	const [selectedCircle, setSelectedCircle] = useState<{
		circleId: string;
		circleName: string;
	} | null>(null);
	const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
	const [circlePayoutDetails, setCirclePayoutDetails] =
		useState<CirclePayoutDetailsResponse | null>(null);
	const [modalPayoutPage, setModalPayoutPage] = useState(0);
	const [modalPayoutRowsPerPage, setModalPayoutRowsPerPage] = useState(10);

	// Pagination state
	const [completedPage, setCompletedPage] = useState(0);
	const [completedRowsPerPage, setCompletedRowsPerPage] = useState(10);
	const [missedPage, setMissedPage] = useState(0);
	const [missedRowsPerPage, setMissedRowsPerPage] = useState(10);
	const [failedPage, setFailedPage] = useState(0);
	const [failedRowsPerPage, setFailedRowsPerPage] = useState(10);
	const [upcomingPage, setUpcomingPage] = useState(0);
	const [upcomingRowsPerPage, setUpcomingRowsPerPage] = useState(10);
	const [retryablePage, setRetryablePage] = useState(0);
	const [retryableRowsPerPage, setRetryableRowsPerPage] = useState(10);

	// Retry confirmation dialog
	const [retryDialogOpen, setRetryDialogOpen] = useState(false);
	const [retryTarget, setRetryTarget] = useState<{
		type: "date" | "specific" | "all";
		date?: string;
		cycleId?: string;
		payoutIndex?: number;
		memberName?: string;
	} | null>(null);

	// Success/Error snackbar state
	const [showRetrySuccess, setShowRetrySuccess] = useState(false);

	// Failure reason popover state
	const [failureReasonAnchor, setFailureReasonAnchor] =
		useState<HTMLElement | null>(null);
	const [failureReasonData, setFailureReasonData] = useState<{
		reason: string;
		memberName: string;
		circleName: string;
		status: string;
		date?: string;
	} | null>(null);

	const handleFailureReasonClick = (
		event: React.MouseEvent<HTMLElement>,
		data: {
			reason: string;
			memberName: string;
			circleName: string;
			status: string;
			date?: string;
		},
	) => {
		setFailureReasonAnchor(event.currentTarget);
		setFailureReasonData(data);
	};

	const handleFailureReasonClose = () => {
		setFailureReasonAnchor(null);
		setFailureReasonData(null);
	};

	const isFailureReasonOpen = Boolean(failureReasonAnchor);

	// Fetch data
	const fetchPayoutOverview = useCallback(() => {
		dispatch(getPayoutOverviewAsAdmin());
	}, [dispatch]);

	const fetchRetryablePayouts = useCallback(() => {
		dispatch(getRetryablePayoutsAsAdmin());
	}, [dispatch]);

	useEffect(() => {
		fetchPayoutOverview();
		fetchRetryablePayouts();
	}, [fetchPayoutOverview, fetchRetryablePayouts]);

	// Show success message when retry completes
	useEffect(() => {
		if (lastRetryResult || bulkRetryResult) {
			setShowRetrySuccess(true);
			// Refresh data after retry
			fetchPayoutOverview();
			fetchRetryablePayouts();
		}
	}, [
		lastRetryResult,
		bulkRetryResult,
		fetchPayoutOverview,
		fetchRetryablePayouts,
	]);

	const handleTabChange = (
		_event: React.SyntheticEvent,
		newValue: TabValue,
	) => {
		setActiveTab(newValue);
		setSearchQuery("");
	};

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(event.target.value);
	};

	const handleRefresh = () => {
		setDismissedError(false);
		setShowRetrySuccess(false);
		fetchPayoutOverview();
		fetchRetryablePayouts();
	};

	// Circle Modal Handlers
	const handleCircleCardClick = useCallback(
		async (circleId: string, circleName: string) => {
			setSelectedCircle({ circleId, circleName });
			setSelectedDate(null);
			setModalPayoutPage(0);
			setCircleModalOpen(true);

			// Fetch circle payout details
			const result = await dispatch(
				getCirclePayoutDetailsAsAdmin({ circleId }),
			);
			if (result.payload?.data) {
				setCirclePayoutDetails(result.payload.data);
			}
		},
		[dispatch],
	);

	const handleCircleModalClose = () => {
		setCircleModalOpen(false);
		setSelectedCircle(null);
		setSelectedDate(null);
		setCirclePayoutDetails(null);
		setModalPayoutPage(0);
	};

	const handleDateSelect = useCallback(
		async (date: Dayjs | null) => {
			setSelectedDate(date);
			setModalPayoutPage(0);

			if (selectedCircle && date) {
				const formattedDate = date.format("YYYY-MM-DD");
				const result = await dispatch(
					getCirclePayoutDetailsAsAdmin({
						circleId: selectedCircle.circleId,
						selectedDate: formattedDate,
					}),
				);
				if (result.payload?.data) {
					setCirclePayoutDetails(result.payload.data);
				}
			} else if (selectedCircle && !date) {
				// Clear date filter - fetch all
				const result = await dispatch(
					getCirclePayoutDetailsAsAdmin({
						circleId: selectedCircle.circleId,
					}),
				);
				if (result.payload?.data) {
					setCirclePayoutDetails(result.payload.data);
				}
			}
		},
		[dispatch, selectedCircle],
	);

	const handleClearDateFilter = useCallback(async () => {
		setSelectedDate(null);
		setModalPayoutPage(0);

		if (selectedCircle) {
			const result = await dispatch(
				getCirclePayoutDetailsAsAdmin({
					circleId: selectedCircle.circleId,
				}),
			);
			if (result.payload?.data) {
				setCirclePayoutDetails(result.payload.data);
			}
		}
	}, [dispatch, selectedCircle]);

	// Retry handlers
	const handleRetryClick = (target: typeof retryTarget) => {
		setRetryTarget(target);
		setRetryDialogOpen(true);
	};

	const handleRetryConfirm = async () => {
		if (!retryTarget) return;

		setRetryDialogOpen(false);

		if (retryTarget.type === "all") {
			await dispatch(retryAllPayouts());
		} else if (retryTarget.type === "date" && retryTarget.date) {
			await dispatch(retryPayoutsForDate({ date: retryTarget.date }));
		} else if (
			retryTarget.type === "specific" &&
			retryTarget.cycleId !== undefined &&
			retryTarget.payoutIndex !== undefined
		) {
			await dispatch(
				retrySpecificPayout({
					cycleId: retryTarget.cycleId,
					payoutIndex: retryTarget.payoutIndex,
				}),
			);
		}

		setRetryTarget(null);
	};

	const handleRetryCancel = () => {
		setRetryDialogOpen(false);
		setRetryTarget(null);
	};

	// Filter payouts based on search query
	const filterPayouts = (payouts: PayoutRecord[]): PayoutRecord[] => {
		if (!searchQuery) return payouts;
		const query = searchQuery.toLowerCase();
		return payouts.filter((payout) => {
			const memberName = (payout.memberName || "").toLowerCase();
			const memberEmail = (payout.memberEmail || "").toLowerCase();
			const circleName = (payout.circleName || "").toLowerCase();
			return (
				memberName.includes(query) ||
				memberEmail.includes(query) ||
				circleName.includes(query)
			);
		});
	};

	const filterRetryablePayouts = (
		payouts: RetryablePayoutRecord[],
	): RetryablePayoutRecord[] => {
		if (!searchQuery) return payouts;
		const query = searchQuery.toLowerCase();
		return payouts.filter((payout) => {
			const memberName = (payout.memberName || "").toLowerCase();
			const memberEmail = (payout.memberEmail || "").toLowerCase();
			const circleName = (payout.circleName || "").toLowerCase();
			return (
				memberName.includes(query) ||
				memberEmail.includes(query) ||
				circleName.includes(query)
			);
		});
	};

	// Status chip helper
	const getStatusChip = (status: string) => {
		switch (status?.toUpperCase()) {
			case "COMPLETED":
				return (
					<Chip
						label="Completed"
						color="success"
						size="small"
						icon={<CheckCircleIcon />}
					/>
				);
			case "FAILED":
				return (
					<Chip
						label="Failed"
						color="error"
						size="small"
						icon={<ErrorIcon />}
					/>
				);
			case "MISSED":
				return (
					<Chip
						label="Missed"
						color="warning"
						size="small"
						icon={<WarningAmberIcon />}
					/>
				);
			case "DUE_TODAY":
				return (
					<Chip
						label="Due Today"
						color="info"
						size="small"
						icon={<TodayIcon />}
					/>
				);
			case "UPCOMING":
				return (
					<Chip
						label="Upcoming"
						color="default"
						size="small"
						icon={<ScheduleIcon />}
					/>
				);
			default:
				return <Chip label={status || "Unknown"} size="small" />;
		}
	};

	// Note: amounts here are already in dollars despite some field names suggesting cents

	// Render Overview Tab
	const renderOverviewTab = () => {
		const summary = payoutOverview?.summary;
		const byCircle = payoutOverview?.byCircle || [];

		return (
			<Stack spacing={3}>
				{/* Summary Cards */}
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={3}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "primary.lighter",
									}}>
									<TrendingUpIcon color="primary" />
								</Box>
								<Box>
									<Typography variant="h5" fontWeight="bold">
										{summary?.totalScheduledPayouts || 0}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Total Payouts
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "success.lighter",
									}}>
									<CheckCircleIcon color="success" />
								</Box>
								<Box>
									<Typography
										variant="h5"
										fontWeight="bold"
										color="success.main">
										{summary?.completed || 0}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Completed
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "warning.lighter",
									}}>
									<WarningAmberIcon color="warning" />
								</Box>
								<Box>
									<Typography
										variant="h5"
										fontWeight="bold"
										color="warning.main">
										{summary?.missed || 0}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Missed
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "error.lighter",
									}}>
									<ErrorIcon color="error" />
								</Box>
								<Box>
									<Typography variant="h5" fontWeight="bold" color="error.main">
										{summary?.failed || 0}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Failed
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
				</Grid>

				{/* Second Row of Summary Cards */}
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={3}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "info.lighter",
									}}>
									<TodayIcon color="info" />
								</Box>
								<Box>
									<Typography variant="h5" fontWeight="bold" color="info.main">
										{summary?.dueToday || 0}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Due Today
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "grey.100",
									}}>
									<ScheduleIcon color="action" />
								</Box>
								<Box>
									<Typography variant="h5" fontWeight="bold">
										{summary?.upcoming || 0}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Upcoming
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "success.lighter",
									}}>
									<AttachMoneyIcon color="success" />
								</Box>
								<Box>
									<Typography
										variant="h5"
										fontWeight="bold"
										color="success.main">
										{formatCurrency(summary?.totalPaidOutCents || 0)}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Total Paid Out
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "primary.lighter",
									}}>
									<TrendingUpIcon color="primary" />
								</Box>
								<Box>
									<Typography
										variant="h5"
										fontWeight="bold"
										color="primary.main">
										{summary?.completionRate || "0"}%
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Completion Rate
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
				</Grid>

				{/* Retry All Button if there are retryable payouts */}
				{retryablePayouts && retryablePayouts.total > 0 && (
					<Card
						sx={{
							p: 2,
							bgcolor: "warning.lighter",
							border: 1,
							borderColor: "warning.light",
						}}>
						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems="center">
							<Stack direction="row" spacing={2} alignItems="center">
								<WarningAmberIcon color="warning" />
								<Box>
									<Typography variant="subtitle1" fontWeight="bold">
										{retryablePayouts.total} Retryable Payout(s)
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{retryablePayouts.missed?.count || 0} missed,{" "}
										{retryablePayouts.failed?.count || 0} failed
									</Typography>
								</Box>
							</Stack>
							<Button
								variant="contained"
								color="warning"
								startIcon={
									isRetrying ?
										<CircularProgress size={16} color="inherit" />
										: <ReplayIcon />
								}
								onClick={() => handleRetryClick({ type: "all" })}
								disabled={isRetrying}>
								{isRetrying ? "Retrying..." : "Retry All"}
							</Button>
						</Stack>
					</Card>
				)}

				{/* Payouts by Circle */}
				<Typography variant="h6" sx={{ mt: 2 }}>
					Payouts by Circle
				</Typography>
				<Grid container spacing={2}>
					{byCircle.length === 0 ?
						<Grid item xs={12}>
							<Card sx={{ p: 4, textAlign: "center" }}>
								<Typography variant="body2" color="text.secondary">
									No circle data available
								</Typography>
							</Card>
						</Grid>
						: byCircle.map((circle: CirclePayoutSummary) => (
							<Grid item xs={12} md={6} lg={4} key={circle.circleId}>
								<Card
									sx={{
										p: 2,
										height: "100%",
										cursor: "pointer",
										transition: "all 0.2s ease-in-out",
										"&:hover": {
											transform: "translateY(-2px)",
											boxShadow: 4,
											borderColor: "primary.main",
										},
									}}
									onClick={() =>
										handleCircleCardClick(circle.circleId, circle.circleName)
									}>
									<Stack spacing={2}>
										<Stack
											direction="row"
											justifyContent="space-between"
											alignItems="center">
											<Typography variant="h6" noWrap sx={{ maxWidth: "70%" }}>
												{circle.circleName}
											</Typography>
											<Chip
												label={`${circle.totalPayouts} payouts`}
												size="small"
												color="primary"
												variant="outlined"
											/>
										</Stack>

										<Divider />

										<Grid container spacing={1}>
											<Grid item xs={6}>
												<Stack direction="row" spacing={1} alignItems="center">
													<CheckCircleIcon fontSize="small" color="success" />
													<Typography variant="body2">
														{circle.completed} completed
													</Typography>
												</Stack>
											</Grid>
											<Grid item xs={6}>
												<Stack direction="row" spacing={1} alignItems="center">
													<WarningAmberIcon fontSize="small" color="warning" />
													<Typography variant="body2">
														{circle.missed} missed
													</Typography>
												</Stack>
											</Grid>
											<Grid item xs={6}>
												<Stack direction="row" spacing={1} alignItems="center">
													<ErrorIcon fontSize="small" color="error" />
													<Typography variant="body2">
														{circle.failed} failed
													</Typography>
												</Stack>
											</Grid>
											<Grid item xs={6}>
												<Stack direction="row" spacing={1} alignItems="center">
													<ScheduleIcon fontSize="small" color="action" />
													<Typography variant="body2">
														{circle.upcoming} upcoming
													</Typography>
												</Stack>
											</Grid>
										</Grid>

										<Box
											sx={{
												p: 1.5,
												borderRadius: 1,
												bgcolor: "success.lighter",
												textAlign: "center",
											}}>
											<Typography variant="h6" color="success.main">
												{formatCurrency(circle.totalPaidCents)}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												Total Paid
											</Typography>
										</Box>

										{/* Member count */}
										<Stack
											direction="row"
											justifyContent="space-between"
											alignItems="center">
											<Stack direction="row" spacing={1} alignItems="center">
												<GroupIcon fontSize="small" color="action" />
												<Typography variant="body2" color="text.secondary">
													{circle.members?.length || 0} members
												</Typography>
											</Stack>
											<Tooltip title="View payout details">
												<ArrowForwardIcon fontSize="small" color="primary" />
											</Tooltip>
										</Stack>
									</Stack>
								</Card>
							</Grid>
						))
					}
				</Grid>
			</Stack>
		);
	};

	// Render Payouts Table (reusable for completed, missed, failed, upcoming)
	const renderPayoutsTable = (
		payouts: PayoutRecord[],
		page: number,
		rowsPerPage: number,
		onPageChange: (page: number) => void,
		onRowsPerPageChange: (rowsPerPage: number) => void,
		showRetryButton: boolean = false,
	) => {
		const filteredPayouts = filterPayouts(payouts);
		const paginatedPayouts = filteredPayouts.slice(
			page * rowsPerPage,
			page * rowsPerPage + rowsPerPage,
		);

		return (
			<Card>
				<Box sx={{ overflowX: "auto" }}>
					<Table sx={{ minWidth: "900px" }}>
						<TableHead>
							<TableRow>
								<TableCell>Member</TableCell>
								<TableCell>Email</TableCell>
								<TableCell>Circle</TableCell>
								<TableCell>Payout #</TableCell>
								<TableCell>Amount</TableCell>
								<TableCell>Status</TableCell>
								<TableCell>Date</TableCell>
								{showRetryButton && <TableCell>Actions</TableCell>}
							</TableRow>
						</TableHead>
						<TableBody>
							{paginatedPayouts.length === 0 ?
								<TableRow>
									<TableCell colSpan={showRetryButton ? 8 : 7} align="center">
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ py: 4 }}>
											No payouts found
										</Typography>
									</TableCell>
								</TableRow>
								: paginatedPayouts.map((payout, index) => (
									<TableRow
										hover
										key={`${payout.cycleId}-${payout.payoutNumber}-${index}`}>
										<TableCell>
											<Stack direction="row" spacing={1} alignItems="center">
												<Avatar sx={{ width: 32, height: 32 }}>
													{payout.memberName?.charAt(0) || "?"}
												</Avatar>
												<Typography variant="subtitle2">
													{payout.memberName || "N/A"}
												</Typography>
											</Stack>
										</TableCell>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												{payout.memberEmail || "N/A"}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2">
												{payout.circleName || "N/A"}
											</Typography>
										</TableCell>
										<TableCell>
											<Chip
												label={`#${payout.payoutNumber} (${payout.rotationIndex}/${payout.totalMembers})`}
												size="small"
												variant="outlined"
											/>
										</TableCell>
										<TableCell>
											<Typography variant="body2" fontWeight="medium">
												{payout.amount ? formatCurrency(payout.amount) : "—"}
											</Typography>
										</TableCell>
										<TableCell>{getStatusChip(payout.status)}</TableCell>
										<TableCell>
											<Typography variant="body2">
												{payout.dateFormatted ||
													dayjs(payout.date).format("MMM D, YYYY")}
											</Typography>
										</TableCell>
										{showRetryButton && (
											<TableCell>
												<Tooltip title="Retry this payout">
													<IconButton
														size="small"
														color="primary"
														onClick={() =>
															handleRetryClick({
																type: "specific",
																cycleId: payout.cycleId,
																payoutIndex: payout.payoutNumber - 1,
																memberName: payout.memberName,
															})
														}
														disabled={isRetrying}>
														<ReplayIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											</TableCell>
										)}
									</TableRow>
								))
							}
						</TableBody>
					</Table>
				</Box>
				<Divider />
				<TablePagination
					component="div"
					count={filteredPayouts.length}
					onPageChange={(_, newPage) => onPageChange(newPage)}
					onRowsPerPageChange={(e) => {
						onRowsPerPageChange(parseInt(e.target.value, 10));
						onPageChange(0);
					}}
					page={page}
					rowsPerPage={rowsPerPage}
					rowsPerPageOptions={[5, 10, 25, 50]}
				/>
			</Card>
		);
	};

	// Render Completed Tab
	const renderCompletedTab = () => {
		const completed = payoutOverview?.past?.completed || [];

		return (
			<Stack spacing={3}>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2 }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<CheckCircleIcon color="success" />
								<Box>
									<Typography variant="h5" color="success.main">
										{completed.length}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Completed Payouts
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2 }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<AttachMoneyIcon color="success" />
								<Box>
									<Typography variant="h5" color="success.main">
										{formatCurrency(
											completed.reduce((sum, p) => sum + (p.amount || 0), 0),
										)}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Total Paid Out
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
				</Grid>

				{renderPayoutsTable(
					completed,
					completedPage,
					completedRowsPerPage,
					setCompletedPage,
					setCompletedRowsPerPage,
				)}
			</Stack>
		);
	};

	// Render Missed Tab
	const renderMissedTab = () => {
		const missed = payoutOverview?.past?.missed || [];

		return (
			<Stack spacing={3}>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2 }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<WarningAmberIcon color="warning" />
								<Box>
									<Typography variant="h5" color="warning.main">
										{missed.length}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Missed Payouts
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
				</Grid>

				{missed.length > 0 && (
					<Alert severity="warning" icon={<WarningAmberIcon />}>
						These payouts were scheduled but never processed. You can retry them
						individually or all at once.
					</Alert>
				)}

				{renderPayoutsTable(
					missed,
					missedPage,
					missedRowsPerPage,
					setMissedPage,
					setMissedRowsPerPage,
					true, // Show retry button
				)}
			</Stack>
		);
	};

	// Render Failed Tab
	const renderFailedTab = () => {
		const failed = payoutOverview?.past?.failed || [];

		return (
			<Stack spacing={3}>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2 }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<ErrorIcon color="error" />
								<Box>
									<Typography variant="h5" color="error.main">
										{failed.length}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Failed Payouts
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
				</Grid>

				{failed.length > 0 && (
					<Alert severity="error" icon={<ErrorIcon />}>
						These payouts failed during processing. Check the failure reason and
						retry after fixing the issue.
					</Alert>
				)}

				<Card>
					<Box sx={{ overflowX: "auto" }}>
						<Table sx={{ minWidth: "1000px" }}>
							<TableHead>
								<TableRow>
									<TableCell>Member</TableCell>
									<TableCell>Email</TableCell>
									<TableCell>Circle</TableCell>
									<TableCell>Payout #</TableCell>
									<TableCell>Status</TableCell>
									<TableCell>Failure Reason</TableCell>
									<TableCell>Date</TableCell>
									<TableCell>Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{filterPayouts(failed).length === 0 ?
									<TableRow>
										<TableCell colSpan={8} align="center">
											<Stack spacing={1} alignItems="center" sx={{ py: 4 }}>
												<CheckCircleIcon
													sx={{ fontSize: 48, color: "success.main" }}
												/>
												<Typography variant="body2" color="text.secondary">
													No failed payouts found
												</Typography>
											</Stack>
										</TableCell>
									</TableRow>
									: filterPayouts(failed)
										.slice(
											failedPage * failedRowsPerPage,
											failedPage * failedRowsPerPage + failedRowsPerPage,
										)
										.map((payout, index) => (
											<TableRow
												hover
												key={`${payout.cycleId}-${payout.payoutNumber}-${index}`}>
												<TableCell>
													<Stack
														direction="row"
														spacing={1}
														alignItems="center">
														<Avatar sx={{ width: 32, height: 32 }}>
															{payout.memberName?.charAt(0) || "?"}
														</Avatar>
														<Typography variant="subtitle2">
															{payout.memberName || "N/A"}
														</Typography>
													</Stack>
												</TableCell>
												<TableCell>
													<Typography variant="body2" color="text.secondary">
														{payout.memberEmail || "N/A"}
													</Typography>
												</TableCell>
												<TableCell>
													<Typography variant="body2">
														{payout.circleName || "N/A"}
													</Typography>
												</TableCell>
												<TableCell>
													<Chip
														label={`#${payout.payoutNumber}`}
														size="small"
														variant="outlined"
													/>
												</TableCell>
												<TableCell>{getStatusChip(payout.status)}</TableCell>
												<TableCell>
													<Chip
														label={
															<Stack
																direction="row"
																spacing={0.5}
																alignItems="center">
																<Typography
																	variant="caption"
																	sx={{
																		maxWidth: 120,
																		whiteSpace: "nowrap",
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																	}}>
																	{(
																		payout.failureReason || "Unknown error"
																	).substring(0, 20)}
																	{(payout.failureReason || "").length > 20 ?
																		"..."
																		: ""}
																</Typography>
																<InfoOutlinedIcon sx={{ fontSize: 14 }} />
															</Stack>
														}
														size="small"
														color="error"
														variant="outlined"
														onClick={(e) =>
															handleFailureReasonClick(e, {
																reason: payout.failureReason || "Unknown error",
																memberName: payout.memberName || "N/A",
																circleName: payout.circleName || "N/A",
																status: payout.status,
																date:
																	payout.dateFormatted ||
																	dayjs(payout.date).format("MMM D, YYYY"),
															})
														}
														sx={{
															cursor: "pointer",
															"&:hover": {
																backgroundColor: "error.light",
																color: "error.contrastText",
															},
														}}
													/>
												</TableCell>
												<TableCell>
													<Typography variant="body2">
														{payout.dateFormatted ||
															dayjs(payout.date).format("MMM D, YYYY")}
													</Typography>
												</TableCell>
												<TableCell>
													<Tooltip title="Retry this payout">
														<IconButton
															size="small"
															color="primary"
															onClick={() =>
																handleRetryClick({
																	type: "specific",
																	cycleId: payout.cycleId,
																	payoutIndex: payout.payoutNumber - 1,
																	memberName: payout.memberName,
																})
															}
															disabled={isRetrying}>
															<ReplayIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												</TableCell>
											</TableRow>
										))
								}
							</TableBody>
						</Table>
					</Box>
					<Divider />
					<TablePagination
						component="div"
						count={filterPayouts(failed).length}
						onPageChange={(_, newPage) => setFailedPage(newPage)}
						onRowsPerPageChange={(e) => {
							setFailedRowsPerPage(parseInt(e.target.value, 10));
							setFailedPage(0);
						}}
						page={failedPage}
						rowsPerPage={failedRowsPerPage}
						rowsPerPageOptions={[5, 10, 25, 50]}
					/>
				</Card>
			</Stack>
		);
	};

	// Render Today Tab
	const renderTodayTab = () => {
		const today = payoutOverview?.today || [];

		return (
			<Stack spacing={3}>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2 }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<TodayIcon color="info" />
								<Box>
									<Typography variant="h5" color="info.main">
										{today.length}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Payouts Due Today
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
				</Grid>

				{today.length === 0 ?
					<Card sx={{ p: 4, textAlign: "center" }}>
						<Stack spacing={1} alignItems="center">
							<CheckCircleIcon sx={{ fontSize: 48, color: "success.main" }} />
							<Typography variant="body2" color="text.secondary">
								No payouts scheduled for today
							</Typography>
						</Stack>
					</Card>
					: <Card>
						<Box sx={{ overflowX: "auto" }}>
							<Table sx={{ minWidth: "800px" }}>
								<TableHead>
									<TableRow>
										<TableCell>Member</TableCell>
										<TableCell>Email</TableCell>
										<TableCell>Circle</TableCell>
										<TableCell>Payout #</TableCell>
										<TableCell>Status</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{filterPayouts(today).map((payout, index) => (
										<TableRow
											hover
											key={`${payout.cycleId}-${payout.payoutNumber}-${index}`}>
											<TableCell>
												<Stack direction="row" spacing={1} alignItems="center">
													<Avatar sx={{ width: 32, height: 32 }}>
														{payout.memberName?.charAt(0) || "?"}
													</Avatar>
													<Typography variant="subtitle2">
														{payout.memberName || "N/A"}
													</Typography>
												</Stack>
											</TableCell>
											<TableCell>
												<Typography variant="body2" color="text.secondary">
													{payout.memberEmail || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{payout.circleName || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Chip
													label={`#${payout.payoutNumber} (${payout.rotationIndex}/${payout.totalMembers})`}
													size="small"
													variant="outlined"
												/>
											</TableCell>
											<TableCell>{getStatusChip(payout.status)}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</Box>
					</Card>
				}
			</Stack>
		);
	};

	// Render Upcoming Tab
	const renderUpcomingTab = () => {
		const upcoming = payoutOverview?.upcoming || [];

		return (
			<Stack spacing={3}>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2 }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<ScheduleIcon color="action" />
								<Box>
									<Typography variant="h5">{upcoming.length}</Typography>
									<Typography variant="body2" color="text.secondary">
										Upcoming Payouts
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
				</Grid>

				{renderPayoutsTable(
					upcoming,
					upcomingPage,
					upcomingRowsPerPage,
					setUpcomingPage,
					setUpcomingRowsPerPage,
				)}
			</Stack>
		);
	};

	// Render Retryable Tab
	const renderRetryableTab = () => {
		const allRetryable = retryablePayouts?.all || [];
		const missed = retryablePayouts?.missed || { count: 0, payouts: [] };
		const failed = retryablePayouts?.failed || { count: 0, payouts: [] };

		const filteredRetryable = filterRetryablePayouts(allRetryable);
		const paginatedRetryable = filteredRetryable.slice(
			retryablePage * retryableRowsPerPage,
			retryablePage * retryableRowsPerPage + retryableRowsPerPage,
		);

		return (
			<Stack spacing={3}>
				{/* Summary Cards */}
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2 }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<ReplayIcon color="primary" />
								<Box>
									<Typography variant="h5" color="primary.main">
										{retryablePayouts?.total || 0}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Total Retryable
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2 }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<WarningAmberIcon color="warning" />
								<Box>
									<Typography variant="h5" color="warning.main">
										{missed.count}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Missed
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2 }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<ErrorIcon color="error" />
								<Box>
									<Typography variant="h5" color="error.main">
										{failed.count}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Failed
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
				</Grid>

				{/* Retry All Button */}
				{allRetryable.length > 0 && (
					<Card sx={{ p: 2 }}>
						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems="center">
							<Typography variant="body1">
								Click the button to retry all {allRetryable.length} payout(s)
							</Typography>
							<Button
								variant="contained"
								color="primary"
								startIcon={
									isRetrying ?
										<CircularProgress size={16} color="inherit" />
										: <ReplayIcon />
								}
								onClick={() => handleRetryClick({ type: "all" })}
								disabled={isRetrying}>
								{isRetrying ? "Retrying..." : "Retry All Payouts"}
							</Button>
						</Stack>
					</Card>
				)}

				{/* Retryable Payouts Table */}
				<Card>
					<Box sx={{ overflowX: "auto" }}>
						<Table sx={{ minWidth: "1100px" }}>
							<TableHead>
								<TableRow>
									<TableCell>Member</TableCell>
									<TableCell>Email</TableCell>
									<TableCell>Circle</TableCell>
									<TableCell>Payout Index</TableCell>
									<TableCell>Status</TableCell>
									<TableCell>Failure Reason</TableCell>
									<TableCell>Days Overdue</TableCell>
									<TableCell>Stripe Account</TableCell>
									<TableCell>Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{paginatedRetryable.length === 0 ?
									<TableRow>
										<TableCell colSpan={9} align="center">
											<Stack spacing={1} alignItems="center" sx={{ py: 4 }}>
												<CheckCircleIcon
													sx={{ fontSize: 48, color: "success.main" }}
												/>
												<Typography variant="body2" color="text.secondary">
													No retryable payouts found
												</Typography>
											</Stack>
										</TableCell>
									</TableRow>
									: paginatedRetryable.map((payout, index) => (
										<TableRow
											hover
											key={`${payout.cycleId}-${payout.payoutIndex}-${index}`}>
											<TableCell>
												<Stack direction="row" spacing={1} alignItems="center">
													<Avatar sx={{ width: 32, height: 32 }}>
														{payout.memberName?.charAt(0) || "?"}
													</Avatar>
													<Typography variant="subtitle2">
														{payout.memberName || "N/A"}
													</Typography>
												</Stack>
											</TableCell>
											<TableCell>
												<Typography variant="body2" color="text.secondary">
													{payout.memberEmail || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{payout.circleName || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Chip
													label={`#${payout.payoutIndex + 1}`}
													size="small"
													variant="outlined"
												/>
											</TableCell>
											<TableCell>
												{payout.status === "FAILED" ?
													<Chip
														label="Failed"
														color="error"
														size="small"
														icon={<ErrorIcon />}
													/>
													: <Chip
														label="Missed"
														color="warning"
														size="small"
														icon={<WarningAmberIcon />}
													/>
												}
											</TableCell>
											<TableCell>
												{payout.failureReason ?
													<Chip
														label={
															<Stack
																direction="row"
																spacing={0.5}
																alignItems="center">
																<Typography
																	variant="caption"
																	sx={{
																		maxWidth: 100,
																		whiteSpace: "nowrap",
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																	}}>
																	{payout.failureReason.substring(0, 15)}
																	{payout.failureReason.length > 15 ?
																		"..."
																		: ""}
																</Typography>
																<InfoOutlinedIcon sx={{ fontSize: 14 }} />
															</Stack>
														}
														size="small"
														color="error"
														variant="outlined"
														onClick={(e) =>
															handleFailureReasonClick(e, {
																reason: payout.failureReason || "Unknown error",
																memberName: payout.memberName || "N/A",
																circleName: payout.circleName || "N/A",
																status: payout.status,
																date: payout.payoutDateFormatted,
															})
														}
														sx={{
															cursor: "pointer",
															"&:hover": {
																backgroundColor: "error.light",
																color: "error.contrastText",
															},
														}}
													/>
													: <Typography variant="body2" color="text.secondary">
														—
													</Typography>
												}
											</TableCell>
											<TableCell>
												<Chip
													label={`${payout.daysOverdue} days`}
													size="small"
													color={
														payout.daysOverdue > 7 ? "error"
															: payout.daysOverdue > 3 ?
																"warning"
																: "default"
													}
												/>
											</TableCell>
											<TableCell>
												{payout.memberStripeAccountId ?
													<Chip
														label="Connected"
														color="success"
														size="small"
														variant="outlined"
													/>
													: <Chip
														label="Not Connected"
														color="error"
														size="small"
														variant="outlined"
													/>
												}
											</TableCell>
											<TableCell>
												<Tooltip title="Retry this payout">
													<IconButton
														size="small"
														color="primary"
														onClick={() =>
															handleRetryClick({
																type: "specific",
																cycleId: payout.cycleId,
																payoutIndex: payout.payoutIndex,
																memberName: payout.memberName,
															})
														}
														disabled={
															isRetrying || !payout.memberStripeAccountId
														}>
														<ReplayIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											</TableCell>
										</TableRow>
									))
								}
							</TableBody>
						</Table>
					</Box>
					<Divider />
					<TablePagination
						component="div"
						count={filteredRetryable.length}
						onPageChange={(_, newPage) => setRetryablePage(newPage)}
						onRowsPerPageChange={(e) => {
							setRetryableRowsPerPage(parseInt(e.target.value, 10));
							setRetryablePage(0);
						}}
						page={retryablePage}
						rowsPerPage={retryableRowsPerPage}
						rowsPerPageOptions={[5, 10, 25, 50]}
					/>
				</Card>
			</Stack>
		);
	};

	return (
		<React.Fragment>
			<UserProfileHeader onOpenNav={() => setOpenNav(true)} />
			<Box
				sx={{
					minHeight: 1,
					display: "flex",
					flexDirection: { xs: "column", lg: "row" },
				}}>
				<DrawerNavbar openNav={openNav} onCloseNav={() => setOpenNav(false)} />

				<Main>
					{isLoadingOverview && !payoutOverview ?
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								minHeight: "60vh",
							}}>
							<LoadingSpinner loading={isLoadingOverview} size={45} />
						</Box>
						: <Stack spacing={3}>
							{/* Header */}
							<Stack direction="row" spacing={3}>
								<Stack spacing={1} sx={{ flex: "1 1 auto" }}>
									<Typography variant="h4">Payout Reports</Typography>
									<Typography color="text.secondary" variant="body2">
										View and manage payout history across all circles and cycles
									</Typography>
								</Stack>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<Tooltip title="Refresh">
										<span>
											<IconButton onClick={handleRefresh} disabled={isLoading}>
												{isLoading ?
													<CircularProgress size={24} />
													: <ArrowsClockwise />}
											</IconButton>
										</span>
									</Tooltip>
								</Box>
							</Stack>

							{/* Error Alert */}
							{error && !dismissedError && (
								<Alert severity="error" onClose={() => setDismissedError(true)}>
									{error}
								</Alert>
							)}

							{/* Success Alert */}
							{showRetrySuccess && (lastRetryResult || bulkRetryResult) && (
								<Alert
									severity={
										(
											lastRetryResult?.success ||
											bulkRetryResult?.totalPayoutsProcessed
										) ?
											"success"
											: "error"
									}
									onClose={() => setShowRetrySuccess(false)}>
									<Stack spacing={1}>
										<Typography variant="body2" fontWeight="medium">
											{lastRetryResult?.message ||
												(bulkRetryResult &&
													`Processed ${bulkRetryResult.totalPayoutsProcessed} of ${bulkRetryResult.totalPayoutsAttempted} payouts`)}
										</Typography>
										{lastRetryResult?.details && (
											<Box
												sx={{
													backgroundColor:
														lastRetryResult?.success ? "success.lighter" : (
															"error.lighter"
														),
													borderRadius: 1,
													p: 1.5,
													mt: 0.5,
												}}>
												<Typography
													variant="caption"
													component="div"
													sx={{ mb: 0.5, fontWeight: 600 }}>
													Payout Details:
												</Typography>
												{lastRetryResult.details.memberName && (
													<Typography variant="caption" component="div">
														Member: {lastRetryResult.details.memberName}
													</Typography>
												)}
												{lastRetryResult.details.memberEmail && (
													<Typography variant="caption" component="div">
														Email: {lastRetryResult.details.memberEmail}
													</Typography>
												)}
												{lastRetryResult.details.circleName && (
													<Typography variant="caption" component="div">
														Circle: {lastRetryResult.details.circleName}
													</Typography>
												)}
												{lastRetryResult.details.netAmount !== undefined &&
													lastRetryResult.details.netAmount > 0 && (
														<Typography variant="caption" component="div">
															Amount:{" "}
															{formatCurrency(lastRetryResult.details.netAmount)}
														</Typography>
													)}
												{/* Show payout stats if available */}
												{lastRetryResult.payoutsProcessed !== undefined && (
													<Typography
														variant="caption"
														component="div"
														sx={{ mt: 0.5 }}>
														Processed: {lastRetryResult.payoutsProcessed} |
														Failed: {lastRetryResult.payoutsFailed || 0}
													</Typography>
												)}
											</Box>
										)}
										{/* Show additional error guidance for failed retries */}
										{!lastRetryResult?.success && lastRetryResult?.message && (
											<Typography
												variant="caption"
												color="error.dark"
												sx={{ mt: 0.5 }}>
												💡 Tip: This payout cannot be processed because there
												are no contributions in the pool for this period.
												Members may need to make contributions before a payout
												can be issued.
											</Typography>
										)}
									</Stack>
								</Alert>
							)}

							{/* Search */}
							<Card sx={{ p: 2 }}>
								<OutlinedInput
									value={searchQuery}
									onChange={handleSearchChange}
									fullWidth
									size="small"
									placeholder="Search by member name, email, or circle..."
									startAdornment={
										<InputAdornment position="start">
											<MagnifyingGlassIcon fontSize="var(--icon-fontSize-md)" />
										</InputAdornment>
									}
								/>
							</Card>

							{/* Tabs */}
							<Card>
								<Tabs
									value={activeTab}
									onChange={handleTabChange}
									indicatorColor="primary"
									textColor="primary"
									variant="scrollable"
									scrollButtons="auto">
									<Tab
										label={
											<Stack direction="row" spacing={1} alignItems="center">
												<TrendingUpIcon />
												<span>Overview</span>
											</Stack>
										}
										value="overview"
									/>
									<Tab
										label={
											<Stack direction="row" spacing={1} alignItems="center">
												<CheckCircleIcon />
												<span>Completed</span>
												<Chip
													label={payoutOverview?.summary?.completed || 0}
													size="small"
													color="success"
												/>
											</Stack>
										}
										value="completed"
									/>
									<Tab
										label={
											<Stack direction="row" spacing={1} alignItems="center">
												<WarningAmberIcon />
												<span>Missed</span>
												<Chip
													label={payoutOverview?.summary?.missed || 0}
													size="small"
													color="warning"
												/>
											</Stack>
										}
										value="missed"
									/>
									<Tab
										label={
											<Stack direction="row" spacing={1} alignItems="center">
												<ErrorIcon />
												<span>Failed</span>
												<Chip
													label={payoutOverview?.summary?.failed || 0}
													size="small"
													color="error"
												/>
											</Stack>
										}
										value="failed"
									/>
									<Tab
										label={
											<Stack direction="row" spacing={1} alignItems="center">
												<TodayIcon />
												<span>Today</span>
												<Chip
													label={payoutOverview?.summary?.dueToday || 0}
													size="small"
													color="info"
												/>
											</Stack>
										}
										value="today"
									/>
									<Tab
										label={
											<Stack direction="row" spacing={1} alignItems="center">
												<ScheduleIcon />
												<span>Upcoming</span>
												<Chip
													label={payoutOverview?.summary?.upcoming || 0}
													size="small"
												/>
											</Stack>
										}
										value="upcoming"
									/>
									<Tab
										label={
											<Stack direction="row" spacing={1} alignItems="center">
												<ReplayIcon />
												<span>Retryable</span>
												<Chip
													label={retryablePayouts?.total || 0}
													size="small"
													color="primary"
												/>
											</Stack>
										}
										value="retryable"
									/>
								</Tabs>
							</Card>

							{/* Tab Content */}
							{isLoading ?
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										minHeight: 300,
									}}>
									<CircularProgress />
								</Box>
								: <>
									{activeTab === "overview" && renderOverviewTab()}
									{activeTab === "completed" && renderCompletedTab()}
									{activeTab === "missed" && renderMissedTab()}
									{activeTab === "failed" && renderFailedTab()}
									{activeTab === "today" && renderTodayTab()}
									{activeTab === "upcoming" && renderUpcomingTab()}
									{activeTab === "retryable" && renderRetryableTab()}
								</>
							}
						</Stack>
					}
				</Main>
			</Box>

			{/* Retry Confirmation Dialog */}
			<Dialog open={retryDialogOpen} onClose={handleRetryCancel}>
				<DialogTitle>Confirm Retry</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{retryTarget?.type === "all" &&
							`Are you sure you want to retry all ${retryablePayouts?.total || 0} missed/failed payouts?`}
						{retryTarget?.type === "date" &&
							`Are you sure you want to retry payouts for ${retryTarget.date}?`}
						{retryTarget?.type === "specific" &&
							`Are you sure you want to retry the payout for ${retryTarget.memberName || "this member"}?`}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleRetryCancel} color="inherit">
						Cancel
					</Button>
					<Button
						onClick={handleRetryConfirm}
						color="primary"
						variant="contained"
						autoFocus>
						Retry
					</Button>
				</DialogActions>
			</Dialog>

			{/* Circle Payout Details Modal */}
			<Modal
				open={circleModalOpen}
				onClose={handleCircleModalClose}
				closeAfterTransition
				slots={{ backdrop: Backdrop }}
				slotProps={{
					backdrop: {
						timeout: 500,
					},
				}}>
				<Fade in={circleModalOpen}>
					<Box
						sx={{
							position: "absolute",
							top: "50%",
							left: "50%",
							transform: "translate(-50%, -50%)",
							width: { xs: "95%", md: "90%", lg: "85%" },
							maxWidth: 1400,
							maxHeight: "90vh",
							bgcolor: "background.paper",
							borderRadius: 2,
							boxShadow: 24,
							overflow: "hidden",
							display: "flex",
							flexDirection: "column",
						}}>
						{/* Modal Header */}
						<Box
							sx={{
								p: 2,
								borderBottom: 1,
								borderColor: "divider",
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}>
							<Stack direction="row" spacing={2} alignItems="center">
								<CalendarMonthIcon color="primary" />
								<Box>
									<Typography variant="h6">
										{selectedCircle?.circleName || "Circle"} - Payout History
									</Typography>
									<Typography variant="body2" color="text.secondary">
										View all payouts, filter by date, and see upcoming schedules
									</Typography>
								</Box>
							</Stack>
							<IconButton onClick={handleCircleModalClose}>
								<CloseIcon />
							</IconButton>
						</Box>

						{/* Modal Content */}
						<Box
							sx={{
								flex: 1,
								overflow: "auto",
								p: 2,
							}}>
							{isLoadingCircleDetails && !circlePayoutDetails ?
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										minHeight: 300,
									}}>
									<CircularProgress />
								</Box>
								: <Grid container spacing={3}>
									{/* Left Side - Calendar & Next Payout */}
									<Grid item xs={12} md={4}>
										<Stack spacing={2}>
											{/* Calendar */}
											<Card sx={{ p: 2 }}>
												<Typography
													variant="subtitle1"
													fontWeight="bold"
													gutterBottom>
													<Stack
														direction="row"
														spacing={1}
														alignItems="center">
														<CalendarMonthIcon fontSize="small" />
														<span>Filter by Date</span>
													</Stack>
												</Typography>
												<LocalizationProvider dateAdapter={AdapterDayjs}>
													<DateCalendar
														value={selectedDate}
														onChange={handleDateSelect}
														sx={{
															width: "100%",
															"& .MuiPickersCalendarHeader-root": {
																paddingLeft: 0,
																paddingRight: 0,
															},
														}}
													/>
												</LocalizationProvider>
												{selectedDate && (
													<Box sx={{ mt: 1, textAlign: "center" }}>
														<Chip
															label={`Showing: ${selectedDate.format("MMM D, YYYY")}`}
															onDelete={handleClearDateFilter}
															color="primary"
															size="small"
														/>
													</Box>
												)}
											</Card>

											{/* Next Payout Info */}
											{circlePayoutDetails?.nextPayout && (
												<Card
													sx={{
														p: 2,
														bgcolor: "primary.lighter",
														border: 1,
														borderColor: "primary.light",
													}}>
													<Typography
														variant="subtitle1"
														fontWeight="bold"
														gutterBottom>
														<Stack
															direction="row"
															spacing={1}
															alignItems="center">
															<ArrowForwardIcon
																fontSize="small"
																color="primary"
															/>
															<span>Next Payout</span>
														</Stack>
													</Typography>
													<Stack spacing={1.5}>
														<Stack
															direction="row"
															spacing={1}
															alignItems="center">
															<Avatar sx={{ width: 32, height: 32 }}>
																{circlePayoutDetails.nextPayout.memberName?.charAt(
																	0,
																) || "?"}
															</Avatar>
															<Box>
																<Typography variant="subtitle2">
																	{circlePayoutDetails.nextPayout.memberName}
																</Typography>
																<Typography
																	variant="caption"
																	color="text.secondary">
																	{circlePayoutDetails.nextPayout.memberEmail}
																</Typography>
															</Box>
														</Stack>
														<Divider />
														<Stack
															direction="row"
															justifyContent="space-between">
															<Typography
																variant="body2"
																color="text.secondary">
																Date:
															</Typography>
															<Typography variant="body2" fontWeight="medium">
																{circlePayoutDetails.nextPayout.dateFormatted}
															</Typography>
														</Stack>
														<Stack
															direction="row"
															justifyContent="space-between">
															<Typography
																variant="body2"
																color="text.secondary">
																Payout #:
															</Typography>
															<Chip
																label={`#${circlePayoutDetails.nextPayout.payoutNumber}`}
																size="small"
																color="primary"
															/>
														</Stack>
														<Stack
															direction="row"
															justifyContent="space-between">
															<Typography
																variant="body2"
																color="text.secondary">
																Days Until:
															</Typography>
															<Chip
																label={
																	(
																		circlePayoutDetails.nextPayout.daysUntil ===
																		0
																	) ?
																		"Today"
																		: (
																			circlePayoutDetails.nextPayout.daysUntil ===
																			1
																		) ?
																			"Tomorrow"
																			: `${circlePayoutDetails.nextPayout.daysUntil} days`

																}
																size="small"
																color={
																	(
																		circlePayoutDetails.nextPayout.daysUntil <=
																		3
																	) ?
																		"warning"
																		: "default"
																}
															/>
														</Stack>
													</Stack>
												</Card>
											)}

											{/* Summary Stats */}
											<Card sx={{ p: 2 }}>
												<Typography
													variant="subtitle1"
													fontWeight="bold"
													gutterBottom>
													Summary
												</Typography>
												<Grid container spacing={1}>
													<Grid item xs={6}>
														<Stack
															direction="row"
															spacing={1}
															alignItems="center">
															<CheckCircleIcon
																fontSize="small"
																color="success"
															/>
															<Typography variant="body2">
																{circlePayoutDetails?.summary?.completed || 0}{" "}
																completed
															</Typography>
														</Stack>
													</Grid>
													<Grid item xs={6}>
														<Stack
															direction="row"
															spacing={1}
															alignItems="center">
															<WarningAmberIcon
																fontSize="small"
																color="warning"
															/>
															<Typography variant="body2">
																{circlePayoutDetails?.summary?.missed || 0}{" "}
																missed
															</Typography>
														</Stack>
													</Grid>
													<Grid item xs={6}>
														<Stack
															direction="row"
															spacing={1}
															alignItems="center">
															<ErrorIcon fontSize="small" color="error" />
															<Typography variant="body2">
																{circlePayoutDetails?.summary?.failed || 0}{" "}
																failed
															</Typography>
														</Stack>
													</Grid>
													<Grid item xs={6}>
														<Stack
															direction="row"
															spacing={1}
															alignItems="center">
															<ScheduleIcon fontSize="small" color="action" />
															<Typography variant="body2">
																{circlePayoutDetails?.summary?.upcoming || 0}{" "}
																upcoming
															</Typography>
														</Stack>
													</Grid>
												</Grid>
												<Box
													sx={{
														mt: 2,
														p: 1.5,
														borderRadius: 1,
														bgcolor: "success.lighter",
														textAlign: "center",
													}}>
													<Typography variant="h6" color="success.main">
														{formatCurrency(
															circlePayoutDetails?.summary?.totalPaidCents || 0,
														)}
													</Typography>
													<Typography variant="caption" color="text.secondary">
														Total Paid Out
													</Typography>
												</Box>
											</Card>
										</Stack>
									</Grid>

									{/* Right Side - Payouts Table */}
									<Grid item xs={12} md={8}>
										<Stack spacing={2}>
											{/* Payouts by Month */}
											{circlePayoutDetails?.payoutsByMonth &&
												circlePayoutDetails.payoutsByMonth.length > 0 && (
													<Card sx={{ p: 2 }}>
														<Typography
															variant="subtitle1"
															fontWeight="bold"
															gutterBottom>
															Payouts by Month
														</Typography>
														<Stack
															direction="row"
															spacing={1}
															flexWrap="wrap"
															useFlexGap>
															{circlePayoutDetails.payoutsByMonth.map(
																(monthGroup: CirclePayoutMonthGroup) => (
																	<Chip
																		key={`${monthGroup.year}-${monthGroup.monthNumber}`}
																		label={`${monthGroup.month}: ${monthGroup.summary.total} payouts`}
																		size="small"
																		variant="outlined"
																		color={
																			(
																				monthGroup.summary.completed ===
																				monthGroup.summary.total
																			) ?
																				"success"
																				: monthGroup.summary.failed > 0 ?
																					"error"
																					: "default"
																		}
																		sx={{ mb: 1 }}
																	/>
																),
															)}
														</Stack>
													</Card>
												)}

											{/* Members & Their Payouts */}
											{circlePayoutDetails?.members &&
												circlePayoutDetails.members.length > 0 && (
													<Card sx={{ p: 2 }}>
														<Typography
															variant="subtitle1"
															fontWeight="bold"
															gutterBottom>
															<Stack
																direction="row"
																spacing={1}
																alignItems="center">
																<PersonIcon fontSize="small" />
																<span>Members Payout Summary</span>
															</Stack>
														</Typography>
														<Grid container spacing={1}>
															{circlePayoutDetails.members.map((member) => (
																<Grid item xs={12} sm={6} key={member.memberId}>
																	<Box
																		sx={{
																			p: 1.5,
																			borderRadius: 1,
																			bgcolor: "grey.50",
																			border: 1,
																			borderColor: "grey.200",
																		}}>
																		<Stack
																			direction="row"
																			spacing={1}
																			alignItems="center">
																			<Avatar sx={{ width: 28, height: 28 }}>
																				{member.memberName?.charAt(0) || "?"}
																			</Avatar>
																			<Box sx={{ flex: 1, minWidth: 0 }}>
																				<Typography
																					variant="body2"
																					fontWeight="medium"
																					noWrap>
																					{member.memberName}
																				</Typography>
																				<Stack
																					direction="row"
																					spacing={0.5}
																					flexWrap="wrap">
																					<Chip
																						label={`${member.payouts.completed} ✓`}
																						size="small"
																						color="success"
																						variant="outlined"
																						sx={{
																							height: 20,
																							fontSize: "0.7rem",
																						}}
																					/>
																					{member.payouts.upcoming > 0 && (
																						<Chip
																							label={`${member.payouts.upcoming} ⏰`}
																							size="small"
																							variant="outlined"
																							sx={{
																								height: 20,
																								fontSize: "0.7rem",
																							}}
																						/>
																					)}
																				</Stack>
																			</Box>
																		</Stack>
																	</Box>
																</Grid>
															))}
														</Grid>
													</Card>
												)}

											{/* All Payouts Table */}
											<Card>
												<Box
													sx={{
														p: 2,
														borderBottom: 1,
														borderColor: "divider",
													}}>
													<Typography variant="subtitle1" fontWeight="bold">
														All Payouts
														{selectedDate &&
															` (Filtered: ${selectedDate.format("MMM D, YYYY")})`}
													</Typography>
												</Box>
												<Box sx={{ overflowX: "auto" }}>
													<Table sx={{ minWidth: 600 }}>
														<TableHead>
															<TableRow>
																<TableCell>Member</TableCell>
																<TableCell>Payout #</TableCell>
																<TableCell>Date</TableCell>
																<TableCell>Status</TableCell>
																<TableCell>Amount</TableCell>
															</TableRow>
														</TableHead>
														<TableBody>
															{(
																!circlePayoutDetails?.allPayouts ||
																circlePayoutDetails.allPayouts.length === 0
															) ?
																<TableRow>
																	<TableCell colSpan={5} align="center">
																		<Typography
																			variant="body2"
																			color="text.secondary"
																			sx={{ py: 4 }}>
																			No payouts found
																			{selectedDate &&
																				` for ${selectedDate.format("MMM D, YYYY")}`}
																		</Typography>
																	</TableCell>
																</TableRow>
																: circlePayoutDetails.allPayouts
																	.slice(
																		modalPayoutPage * modalPayoutRowsPerPage,
																		modalPayoutPage * modalPayoutRowsPerPage +
																		modalPayoutRowsPerPage,
																	)
																	.map((payout, index) => (
																		<TableRow
																			hover
																			key={`${payout.cycleId}-${payout.payoutNumber}-${index}`}>
																			<TableCell>
																				<Stack
																					direction="row"
																					spacing={1}
																					alignItems="center">
																					<Avatar
																						sx={{ width: 28, height: 28 }}>
																						{payout.memberName?.charAt(0) ||
																							"?"}
																					</Avatar>
																					<Box>
																						<Typography variant="body2">
																							{payout.memberName || "N/A"}
																						</Typography>
																						<Typography
																							variant="caption"
																							color="text.secondary">
																							{payout.memberEmail}
																						</Typography>
																					</Box>
																				</Stack>
																			</TableCell>
																			<TableCell>
																				<Chip
																					label={`#${payout.payoutNumber}`}
																					size="small"
																					variant="outlined"
																				/>
																			</TableCell>
																			<TableCell>
																				<Typography variant="body2">
																					{payout.dateFormatted ||
																						dayjs(payout.date).format(
																							"MMM D, YYYY",
																						)}
																				</Typography>
																			</TableCell>
																			<TableCell>
																				{getStatusChip(payout.status)}
																			</TableCell>
																			<TableCell>
																				<Typography
																					variant="body2"
																					fontWeight="medium">
																					{payout.amount ?
																						formatCurrency(payout.amount)
																						: "—"}
																				</Typography>
																			</TableCell>
																		</TableRow>
																	))
															}
														</TableBody>
													</Table>
												</Box>
												{circlePayoutDetails?.allPayouts &&
													circlePayoutDetails.allPayouts.length > 0 && (
														<>
															<Divider />
															<TablePagination
																component="div"
																count={circlePayoutDetails.allPayouts.length}
																onPageChange={(_, newPage) =>
																	setModalPayoutPage(newPage)
																}
																onRowsPerPageChange={(e) => {
																	setModalPayoutRowsPerPage(
																		parseInt(e.target.value, 10),
																	);
																	setModalPayoutPage(0);
																}}
																page={modalPayoutPage}
																rowsPerPage={modalPayoutRowsPerPage}
																rowsPerPageOptions={[5, 10, 25, 50]}
															/>
														</>
													)}
											</Card>
										</Stack>
									</Grid>
								</Grid>
							}
						</Box>
					</Box>
				</Fade>
			</Modal>

			{/* Failure Reason Popover */}
			<Popover
				open={isFailureReasonOpen}
				anchorEl={failureReasonAnchor}
				onClose={handleFailureReasonClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
				slotProps={{
					paper: {
						sx: {
							maxWidth: 420,
							borderRadius: 2,
							boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
							border: "1px solid",
							borderColor: "divider",
						},
					},
				}}>
				{failureReasonData && (
					<Box>
						{/* Header */}
						<Box
							sx={{
								px: 2.5,
								py: 1.5,
								background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
								borderRadius: "8px 8px 0 0",
							}}>
							<Stack direction="row" spacing={1.5} alignItems="center">
								<Box
									sx={{
										width: 36,
										height: 36,
										borderRadius: "50%",
										backgroundColor: "rgba(255,255,255,0.2)",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}>
									<ErrorIcon sx={{ color: "white", fontSize: 20 }} />
								</Box>
								<Box sx={{ flex: 1 }}>
									<Typography
										variant="subtitle2"
										sx={{ color: "white", fontWeight: 600 }}>
										Payout Failed
									</Typography>
									<Typography
										variant="caption"
										sx={{ color: "rgba(255,255,255,0.8)" }}>
										{failureReasonData.status === "FAILED" ?
											"Processing Error"
											: "Missed Payout"}
									</Typography>
								</Box>
								<IconButton
									size="small"
									onClick={handleFailureReasonClose}
									sx={{
										color: "white",
										"&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
									}}>
									<CloseIcon fontSize="small" />
								</IconButton>
							</Stack>
						</Box>

						{/* Content */}
						<Box sx={{ p: 2.5 }}>
							{/* Error Message */}
							<Box
								sx={{
									p: 2,
									backgroundColor: "error.lighter",
									borderRadius: 1.5,
									border: "1px solid",
									borderColor: "error.light",
									mb: 2,
								}}>
								<Typography
									variant="body2"
									sx={{
										color: "error.dark",
										lineHeight: 1.6,
										wordBreak: "break-word",
									}}>
									{failureReasonData.reason}
								</Typography>
							</Box>

							{/* Details Grid */}
							<Stack spacing={1.5}>
								<Stack direction="row" spacing={1} alignItems="center">
									<PersonIcon sx={{ fontSize: 18, color: "text.secondary" }} />
									<Typography variant="body2" color="text.secondary">
										Member:
									</Typography>
									<Typography variant="body2" fontWeight="medium">
										{failureReasonData.memberName}
									</Typography>
								</Stack>

								<Stack direction="row" spacing={1} alignItems="center">
									<GroupIcon sx={{ fontSize: 18, color: "text.secondary" }} />
									<Typography variant="body2" color="text.secondary">
										Circle:
									</Typography>
									<Typography variant="body2" fontWeight="medium">
										{failureReasonData.circleName}
									</Typography>
								</Stack>

								{failureReasonData.date && (
									<Stack direction="row" spacing={1} alignItems="center">
										<CalendarMonthIcon
											sx={{ fontSize: 18, color: "text.secondary" }}
										/>
										<Typography variant="body2" color="text.secondary">
											Date:
										</Typography>
										<Typography variant="body2" fontWeight="medium">
											{failureReasonData.date}
										</Typography>
									</Stack>
								)}
							</Stack>
						</Box>

						{/* Footer */}
						<Divider />
						<Box sx={{ px: 2.5, py: 1.5, backgroundColor: "grey.50" }}>
							<Typography variant="caption" color="text.secondary">
								💡 Tip: Resolve the issue above, then click the retry button to
								process this payout.
							</Typography>
						</Box>
					</Box>
				)}
			</Popover>
		</React.Fragment>
	);
};

export default PayOutReports;
